import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import type { BeniSmartCompset } from 'lumen/models';
import type { BenchmarkInsightSettingsModel } from 'lumen/routes/hotel/settings/competitors/benchmark';
import {
  BENI_SMART_COMPSET_BUILDER_MESSAGE_TYPES,
  BENI_SMART_COMPSET_BUILDER_VALIDATION_TIME,
} from 'lumen/utils/constants/benchmark-insight/beni-smart-compset-builder-constants';

export default class HotelSettingsCompetitorsBenchmarkSmartCompsetValidationRoute extends Route {
  @service('benchmark-insight/beni-smart-compset-service')
  declare benchmarkInsightBeniSmartCompsetService: Services['benchmark-insight/beni-smart-compset-service'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  saveWithDelayTask = task(
    async (beniSmartCompset: BeniSmartCompset): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const { displayAlert } = this.benchmarkInsightBeniSmartCompsetService;
      const { name } = beniSmartCompset;

      let isSuccessful = false;
      try {
        const SAVE_DELAY = BENI_SMART_COMPSET_BUILDER_VALIDATION_TIME;
        const startTime = Date.now();

        await beniSmartCompset.save();

        const elapsed = Date.now() - startTime;
        const remaining = Math.max(SAVE_DELAY - elapsed, 0);

        await timeout(remaining);

        displayAlert(
          BENI_SMART_COMPSET_BUILDER_MESSAGE_TYPES.SUCCESS_UPDATE,
          true,
          name,
        );
        isSuccessful = true;
      } catch (error) {
        displayAlert(
          BENI_SMART_COMPSET_BUILDER_MESSAGE_TYPES.FAIL_UPDATE,
          false,
          name,
        );
        beniSmartCompset.rollbackAttributes();
        this.errorTracking.captureException(error);
      } finally {
        this.router.transitionTo('hotel.settings.competitors.benchmark');

        this.usageAnalytics.track('Smart Compset Edited', {
          eventTrigger: 'smart-compset-builder',
          updateSuccessful: isSuccessful,
        });
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/require-await
  async afterModel(model: BenchmarkInsightSettingsModel): Promise<void> {
    const { smartCompset } = model;
    const beniSmartCompset = smartCompset.last?.value ?? null;

    if (beniSmartCompset) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.saveWithDelayTask.perform(beniSmartCompset);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/require-await
  async model() {
    return this.modelFor('hotel.settings.competitors.benchmark');
  }
}
